/**
 * «Eliminar mi cuenta» para TODOS los roles (D7 de Pablo, 26-09-2026).
 *
 * Hasta hoy solo el admin podía pedirlo (decisión del 02-09): el resto no veía el
 * botón… salvo el perito, que sí lo veía y siempre fallaba con «Inténtalo de
 * nuevo» porque la edge respondía 403 `not_admin` (M33 / T10-04). Ahora cualquiera
 * lo pide y le pasa lo mismo que al admin: queda registrada la solicitud en
 * `account_deletion_requests`, le llega el correo de «estamos revisando tu
 * solicitud, te contestamos en 2 días laborables» y a admin@ulture.co el aviso
 * interno. La edge NO borra nada: el borrado lo hace el equipo de Ulture a mano.
 *
 * Aquí vive la llamada y el porqué de cada respuesta, para que la pantalla del
 * admin, del encargado, del trabajador y del perito digan lo mismo.
 */
import { supabase } from "@/integrations/supabase/client";

/** Qué pasó con el correo de confirmación al usuario (lo dice la edge). */
export type DeletionEmailStatus = "sent" | "failed" | "no_mailbox" | "unknown";

export type AccountDeletionResult =
  | { ok: true; userEmail: DeletionEmailStatus }
  | {
      ok: false;
      code: "invalid_password" | "already_pending" | "password_required" | "invalid_name" | "name_required" | "not_available" | "unknown";
    };

/**
 * ¿Confirma con contraseña? Solo quien tiene la identidad «email». Quien se registró con
 * Google o Apple no tiene contraseña y confirma escribiendo su nombre (09-10-2026: antes
 * no podía pedir la baja). La edge decide lo mismo con el usuario del token; esto solo
 * elige qué campo se enseña. Sin usuario cargado, contraseña (lo de siempre).
 */
export function confirmaConContrasena(
  user: { app_metadata?: { providers?: unknown }; identities?: { provider?: string }[] | null } | null | undefined,
): boolean {
  if (!user) return true;
  const providers = Array.isArray(user.app_metadata?.providers) ? (user.app_metadata.providers as string[]) : [];
  const identidades = (user.identities ?? []).map((i) => i.provider ?? "");
  const todos = [...providers, ...identidades];
  return todos.length === 0 || todos.includes("email");
}

/** Lo que escribe la persona: su contraseña o, si entra con Google o Apple, su nombre. */
export type DeletionConfirmation = { password: string } | { nameConfirmation: string };

export async function requestAccountDeletion(confirmation: DeletionConfirmation, language: string): Promise<AccountDeletionResult> {
  if ("password" in confirmation && !confirmation.password.trim()) return { ok: false, code: "password_required" };
  if ("nameConfirmation" in confirmation && !confirmation.nameConfirmation.trim()) return { ok: false, code: "name_required" };
  try {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token;
    const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/request-account-deletion`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ ...confirmation, language }),
    });
    let body: { error?: string; user_email?: string } = {};
    try {
      body = await res.json();
    } catch {
      body = {};
    }
    return interpretDeletionResponse(res.ok, body);
  } catch (err) {
    console.error("Delete account error:", err);
    return { ok: false, code: "unknown" };
  }
}

/** Pura, para poder probarla: status HTTP + cuerpo → resultado. */
export function interpretDeletionResponse(ok: boolean, body: { error?: string; user_email?: string } | null | undefined): AccountDeletionResult {
  if (ok) {
    const e = body?.user_email;
    const userEmail: DeletionEmailStatus = e === "sent" || e === "failed" || e === "no_mailbox" ? e : "unknown";
    return { ok: true, userEmail };
  }
  const error = body?.error;
  if (error === "invalid_password") return { ok: false, code: "invalid_password" };
  if (error === "already_pending") return { ok: false, code: "already_pending" };
  if (error === "Password required") return { ok: false, code: "password_required" };
  if (error === "invalid_name") return { ok: false, code: "invalid_name" };
  if (error === "name_required") return { ok: false, code: "name_required" };
  // La edge vieja (antes de desplegar esta tanda) contesta `not_admin` a quien no es admin.
  if (error === "not_admin") return { ok: false, code: "not_available" };
  return { ok: false, code: "unknown" };
}

/** La clave i18n del mensaje para cada resultado. */
export function accountDeletionMessageKey(result: AccountDeletionResult): string {
  if (result.ok) {
    if (result.userEmail === "no_mailbox") return "settings.account.requestSuccessNoMailbox";
    if (result.userEmail === "failed") return "settings.account.requestSuccessEmailFailed";
    return "settings.account.requestSuccess";
  }
  // `"code" in` en vez de `result.code`: con tsconfig.app.json (sin strictNullChecks)
  // TypeScript no estrecha la unión por `ok` y el editor marcaba error.
  switch ("code" in result ? result.code : undefined) {
    case "invalid_password":
      return "settings.account.invalidPassword";
    case "already_pending":
      return "settings.account.alreadyPending";
    case "password_required":
      return "settings.account.passwordRequired";
    case "invalid_name":
      return "settings.account.invalidName";
    case "name_required":
      return "settings.account.nameRequired";
    case "not_available":
      return "settings.account.requestNotAvailable";
    default:
      return "settings.account.requestError";
  }
}
